import { useRef, useEffect, useCallback, useState } from 'react';
import { findNextFocusableElement } from '@helpers/utils/a11y/focusable-elements';

// Global ref to store the last focused interactive element
// This handles async operations where focus is lost before the modal opens
let lastFocusedElement: HTMLElement | null = null;

// Track the last focused interactive element globally
if (typeof window !== 'undefined') {
  document.addEventListener(
    'focusin',
    (e) => {
      const target = e.target as HTMLElement;
      // Only store interactive elements, not document.body
      if (target && target !== document.body && target.tabIndex >= 0) {
        lastFocusedElement = target;
      }
    },
    true,
  ); // Use capture phase to catch all focus events
}

export const resetLastFocusedElement = () => {
  lastFocusedElement = null;
};

export const useFocusRestore = (
  isOpen: boolean,
  options?: {
    onAfterRestore?: (element: HTMLElement | null, restored: boolean) => void;
  },
) => {
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const { onAfterRestore } = options ?? {};

  useEffect(() => {
    return () => {
      // If component unmounts while open, restore focus
      if (wasOpenRef.current && triggerElementRef.current) {
        // Don't restore if focus has already moved to another interactive element
        const currentActive = document.activeElement as HTMLElement;
        if (currentActive && currentActive !== document.body && currentActive.tabIndex >= 0) {
          return; // User has intentionally focused another element
        }

        const element = triggerElementRef.current;
        const isDisabled = (element as HTMLButtonElement).disabled;
        const isInDOM = document.body.contains(element);

        if (!isDisabled && isInDOM) {
          element.focus();
          return;
        }

        const nextFocusable = findNextFocusableElement(element);

        if (nextFocusable) {
          nextFocusable.focus();
        }
      }
    };
  }, []);

  // Manually restore focus to the triggering element. Useful for components that need to restore focus at a specific time.
  const restoreFocus = useCallback((): boolean => {
    if (!triggerElementRef.current || typeof triggerElementRef.current.focus !== 'function') {
      return false;
    }

    // If focus has already moved to another interactive element (e.g., user clicked another button),
    // don't steal it back. This handles cases like Filter A closing → Filter B opening.
    const currentActive = document.activeElement as HTMLElement;
    if (currentActive && currentActive !== document.body && currentActive.tabIndex >= 0) {
      // User has intentionally focused another element - don't restore
      return false;
    }

    const element = triggerElementRef.current;
    const isDisabled = (element as HTMLButtonElement).disabled;
    const isInDOM = document.body.contains(element);

    if (!isDisabled && isInDOM) {
      element.focus();
      return true;
    }

    const nextFocusable = findNextFocusableElement(element);

    if (nextFocusable) {
      nextFocusable.focus();
      return document.activeElement === nextFocusable;
    }

    return false;
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Use the global last focused element as fallback for async operations
      const currentActive = document.activeElement as HTMLElement;
      if (currentActive && currentActive !== document.body) {
        // If there's a currently focused element (synchronous open), use it
        triggerElementRef.current = currentActive;
      } else if (lastFocusedElement && lastFocusedElement !== document.body) {
        // Otherwise, use the last focused interactive element (async open)
        triggerElementRef.current = lastFocusedElement;
      }
      // If neither condition is met, triggerElementRef remains null
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      // Only restore focus if the component was previously open (not on initial mount)
      // and if we have a trigger element to restore to
      if (triggerElementRef.current) {
        const restored = restoreFocus();
        onAfterRestore?.(triggerElementRef.current, restored);
      }
      triggerElementRef.current = null;
      wasOpenRef.current = false;
    }
  }, [isOpen, onAfterRestore, restoreFocus]);

  return {
    restoreFocus,
    triggerElementRef,
  };
};

// Hook to restore focus to a chosen element from a list after the list updates
export const useFocusRestoreToChosenElement = (isElementProduct: boolean, elementList, idPrefix: string) => {
  const triggerFocusRef = useRef(false);
  const [elementIndex, setElementIndex] = useState<number>(0);
  // When elementList changes, check if we need to restore focus
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (triggerFocusRef.current && elementList?.length > 0) {
      const isValidIndex = Number.isInteger(elementIndex) && elementIndex >= 0 && elementIndex < elementList.length;
      if (!isValidIndex) {
        // Invalid index; do not attempt to focus a non-existent element
        triggerFocusRef.current = false;
        return;
      }
      const elementId = isElementProduct && elementIndex >= 0 ? elementList?.[elementIndex]?.id : elementIndex;
      // We use setTimeout to not interfere with the automatic scrolling behavior of the browser
      // when the element list updates.
      timeout = setTimeout(() => {
        const targetElement = document.getElementById(`${idPrefix}${elementId}`);
        if (targetElement) {
          // Ensure the element is focusable when focused programmatically
          if (targetElement.tabIndex < 0) {
            targetElement.tabIndex = 0;
          }
          targetElement.focus();
        }
        triggerFocusRef.current = false;
      }, 1000);
    }
    // Cleanup timeout on unmount or if elementList changes again
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [elementList]);
  // Function to trigger focus restoration on the next update
  const triggerFocusOnNextUpdate = (index: number) => {
    triggerFocusRef.current = true;
    setElementIndex(index);
  };

  return triggerFocusOnNextUpdate;
};
